import { minorUnitToMajorUnit } from "@repo/system";
import { type CreateOrderBasket } from "~/api/orders/requests";
import { type TransactionItem } from "~/types/events";
import { TRANSACTION_CODE } from "~/utils/constants";

const SKU_MAX_LENGTH = 30;

function declaredUnitDeposit(product: CreateOrderBasket[number]) {
  const declared = [
    { deposit: product.deposit, amount: 1 },
    ...(product.options ?? []).flatMap((option) =>
      option.choices.map((choice) => ({
        deposit: choice.deposit,
        amount: choice.amount
      }))
    )
  ];

  return declared.reduce(
    (acc, { deposit, amount }) =>
      deposit
        ? {
            inclVat: acc.inclVat + deposit.feeInclVat * amount,
            exclVat: acc.exclVat + deposit.feeExclVat * amount
          }
        : acc,
    { inclVat: 0, exclVat: 0 }
  );
}

/**
 * Maps the attended basket to FreedomPay line items.
 *
 * Freeway validates that the lines sum to the amount charged, so every money
 * field here is a line total and `unitPrice` is gross.
 */
export function toFreedomPayItems(
  basketOrder: CreateOrderBasket
): TransactionItem[] {
  return basketOrder.map((product) => {
    // Calculate gross unit price (including VAT)
    // vatRate is stored as a decimal (e.g., 0.25 for 25%)
    const unitGrossPrice = product.unitPrice * (1 + product.vatRate);
    // Calculate VAT for all items (total VAT for the quantity)
    const totalTaxAmount = product.amount * product.unitPrice * product.vatRate;

    // The deposit rides on its product's line rather than its own: a line is
    // identified only by `productSKU`, and a deposit has no product id to put
    // there. Its VAT is whatever the scheme resolved, which is zero for a
    // no-VAT scheme and the difference between the fees otherwise.
    const { inclVat: unitDepositInclVat, exclVat: unitDepositExclVat } =
      declaredUnitDeposit(product);
    const unitDepositVat = unitDepositInclVat - unitDepositExclVat;

    return {
      // TODO: product id can be longer than the required length (30)
      productSKU: product.id.slice(0, SKU_MAX_LENGTH),
      productName: product.name ?? "unknown product name",
      productDescription: product.description ?? "",
      unitPrice: minorUnitToMajorUnit(unitGrossPrice + unitDepositInclVat),
      quantity: product.amount,
      totalAmount: minorUnitToMajorUnit(
        (unitGrossPrice + unitDepositInclVat) * product.amount
      ),
      saleCode: TRANSACTION_CODE.SALE,
      taxAmount: minorUnitToMajorUnit(
        totalTaxAmount + unitDepositVat * product.amount
      )
    };
  });
}
